<template>
  <div v-if="demoData" class="sidebar" :class="{ open: open }">
    <div class="sidebar-bg" @click="$emit('closed')"></div>
    <div class="sidebar-content r-0">
      <!-- Header -->
      <SidebarHeader
        title="Messages"
        :count="chatMessages.length"
        @close="$emit('closed')"
      />

      <!-- Chat messages -->
      <div ref="chatMessagesContainer" class="comments-container flex-1 of-y-s no-scrollbar">
        <div
          v-for="(msg, index) in chatMessages"
          :key="index"
          class="d-f g comment-container m-b ai-fe m-t-xl"
          :class="msg.from === myId ? 'f-right' : 'f-left'"
        >
          <ImageWithInitials
            :src="messageSenderImages[msg.from]"
            :name="messageSenderNames[msg.from]"
            size="30"
            :border="true"
          />

          <div class="message-container d-f fd-c">
            <div class="text-wrap">
              {{ msg.message }}
            </div>
            <p class="font-small m-t-sm">
              {{ messageSenderNames[msg.from] }}
              <TimestampCountdown
                class="mute"
                :start="new Date(msg.timestamp)"
              />
            </p>
          </div>
        </div>
      </div>

      <!-- Input -->
      <ChatInput
        v-if="isReceiver || chatMessages.length > 0"
        :demo-data="demoData"
      />
    </div>
  </div>
</template>

<script>
import ChatInput from "@/components/misc/ChatInput.vue";
import { getSortedObjArrayByChild } from "@/common";
import { mapGetters, mapState } from "vuex";
import ImageWithInitials from "@/components/misc/ImageWithInitials.vue";
import TimestampCountdown from "@/components/misc/TimestampCountdown.vue";
import SidebarHeader from "@/components/navigation/SidebarHeader.vue";

export default {
  name: "ChatSidebar",
  components: {
    TimestampCountdown,
    ImageWithInitials,
    ChatInput,
    SidebarHeader,
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    demoId: {
      default: null,
    },
    demo: {
      default: null,
    },
  },
  data() {
    return {};
  },
  created() {
    this.loadUserData();
  },
  mounted() {
    this.messagesUpdated();

    if (this.open) {
      this.markMessagesViewed();
    }
  },
  methods: {
    loadUserData() {
      if (this.demoData) {
        this.$store.dispatch("cacheUserData", {
          uid: this.demoData.submittedBy,
        });
        this.$store.dispatch("cacheUserData", {
          uid: this.demoData.submittedTo,
        });
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.chatMessagesContainer) {
          this.$refs.chatMessagesContainer.scrollTop =
            this.$refs.chatMessagesContainer.scrollHeight;
        }
      });
    },
    messagesUpdated() {
      this.scrollToBottom();

      // Easier to send last message to parent than calculating it in the parent component
      this.$emit(
        "last-message-update",
        this.lastMessage
          ? {
              profileImage: this.messageSenderImages[this.lastMessage.from],
              message: this.lastMessage.message,
              profileName: this.messageSenderNames[this.lastMessage.from],
              timestamp: new Date(this.lastMessage.timestamp),
              total: this.chatMessages.length,
            }
          : null
      );
    },
    getReviewerNameWithTitle(reviewer) {
      return [
        this.selectedReviewer && this.selectedReviewer?.id === reviewer.id
          ? "You"
          : reviewer.name,
        reviewer.title,
      ]
        .filter(Boolean)
        .join(" - ");
    },
    markMessagesViewed() {
      if (!this.lastMessage) return;

      this.$store.dispatch("setDemoSeenMessagesTimestamp", {
        demoId: this.demoData.id,
        userId: this.myId,
        timestamp: new Date(this.lastMessage.timestamp).getTime(),
      });
    },
  },
  computed: {
    ...mapState(["userType", "selectedReviewer"]),
    ...mapGetters([
      "getUser",
      "myId",
      "isReceiver",
      "activeReviewerId",
      "getDemoById",
      "getProfileNameByUid",
      "getProfileImageByUid",
      "getLabelReviewersByUid",
    ]),

    demoData() {
      return this.demo || this.getDemoById(this.demoId);
    },

    chatMessages() {
      return getSortedObjArrayByChild(this.demoData?.messages || {}, "asc");
    },

    messageSenderNames() {
      return {
        ...Object.fromEntries(
          Object.entries(this.reviewers).map(([id, reviewer]) => {
            return [id, this.getReviewerNameWithTitle(reviewer)];
          })
        ),
        [this.demoData?.submittedBy]: this.submittedByName ?? "-",
      };
    },

    messageSenderImages() {
      return {
        ...Object.fromEntries(
          Object.entries(this.reviewers).map(([id, reviewer]) => [
            id,
            reviewer.profileImage,
          ])
        ),
        [this.demoData?.submittedBy]: this.submittedByImage ?? "-",
      };
    },

    submittedByName() {
      return this.getProfileNameByUid(this.demoData?.submittedBy) || "-";
    },

    submittedByImage() {
      return this.getProfileImageByUid(this.demoData?.submittedBy) || "-";
    },

    reviewers() {
      const labelReviewers = this.getLabelReviewersByUid(this.demoData?.submittedTo);
      if (Object.keys(labelReviewers).length > 0) return labelReviewers;

      const receiverUid = this.demoData?.submittedTo;
      if (receiverUid) {
        const name = this.getProfileNameByUid(receiverUid);
        const profileImage = this.getProfileImageByUid(receiverUid);
        if (name) {
          return { [receiverUid]: { name, profileImage } };
        }
      }

      return {};
    },

    lastMessage() {
      return this.chatMessages[this.chatMessages.length - 1];
    },
  },
  watch: {
    selectedReviewer() {},
    userType() {},

    demoData() {
      this.loadUserData();
    },

    open(newVal) {
      this.scrollToBottom();

      if (newVal) {
        this.markMessagesViewed();
      }
    },

    chatMessages() {
      this.messagesUpdated();
    },

    messageSenderImages() {
      this.messagesUpdated();
    },

    messageSenderNames() {
      this.messagesUpdated();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";
@import "@/scss/_classes.scss";

.chat-message {
  &.f-right {
    flex-direction: row-reverse;
    text-align: right;

    & .message-p {
      border-bottom-right-radius: 0 !important;
    }
  }

  &.f-left {
    flex-direction: row;
    text-align: left;

    & .message-p {
      border-bottom-left-radius: 0 !important;
    }
  }
}
</style>
